import { MEDIA_MOBILE_BREAKPOINT } from "src/apps/config/constants";
import { BaseScreen } from "src/apps/components/baseScreen/BaseScreen";
import styled from "styled-components";
import { Header } from "src/apps/components/header/Header";
import { Models } from "./models/Models";
import { Payslips } from "./payslips/Payslips";
import { DocumentationAreaScreenHeader } from "./DocumentationAreaScreenHeader";
import { useDocumentationArea } from "src/apps/hooks/useDocumentationArea";
import { UploaderProvider } from "src/apps/providers/uploaders/UploaderProvider";
import { IdentityVerifications } from "./identityVerification/IdentityVerifications";
import {
  AnalyticsEvents,
  useSendAnalyticEvent,
} from "src/apps/hooks/analytics/useSendAnalyticEvent";
import { useEffect } from "react";
import { OtherDocuments } from "./otherDocuments/OtherDocuments";
import { colors } from "@permisso/permisso-components";
import { testId } from "src/apps/utils/testId";

export const DocumentationAreaScreen = () => {
  const disableWindowOpenInNewTabForFile = (event: BeforeUnloadEvent) => {
    event.preventDefault();
  };

  const { models, payslips, otherDocuments, identityVerifications, openAdminBanner } =
    useDocumentationArea();
  const { sendEvent } = useSendAnalyticEvent();

  const openAdminBannerState = openAdminBanner?.getStatus() ?? "not_specified";
  const homePageBannerAnalyticsProperties = {
    open_admin_banner_state: openAdminBannerState,
  };

  useEffect(() => {
    sendEvent(AnalyticsEvents.DOCUMENTATION_AREA_HOME_PAGE_OPENED, {
      ...homePageBannerAnalyticsProperties,
    });
  }, []);

  useEffect(() => {
    window.addEventListener("dragover", disableWindowOpenInNewTabForFile);
    window.addEventListener("drop", disableWindowOpenInNewTabForFile);
    return () => {
      window.removeEventListener("dragover", disableWindowOpenInNewTabForFile);
      window.removeEventListener("drop", disableWindowOpenInNewTabForFile);
    };
  }, []);

  return (
    <UploaderProvider>
      <BaseScreen header={<Header showBanner />} backgroundColor={colors.grey200}>
        <BackgroundContainer>
          <MainContainer {...testId("documentation-area-screen")}>
            <HeaderSection>
              <DocumentationAreaScreenHeader />
            </HeaderSection>
            <UploadSections>
              <Models models={models} />
              <Payslips payslips={payslips} />
              <OtherDocuments otherDocuments={otherDocuments} />
              <IdentityVerifications identityVerifications={identityVerifications} />
            </UploadSections>
          </MainContainer>
        </BackgroundContainer>
      </BaseScreen>
    </UploaderProvider>
  );
};

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${colors.grey0};
  padding: 2.5rem;
  width: 73%;
  gap: 2rem;

  @media (max-width: ${MEDIA_MOBILE_BREAKPOINT}rem) {
    box-shadow: unset;
    padding: 0rem;
    width: 100%;
  }
`;

const BackgroundContainer = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  background-color: ${colors.grey200};
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  @media (max-width: ${MEDIA_MOBILE_BREAKPOINT}rem) {
    flex: 1;
    border-radius: unset;
    box-shadow: unset;
    margin: 0;
    min-width: unset;
    width: 100%;
  }
`;

const HeaderSection = styled(Container)`
  display: flex;
  flex-direction: column;
  background-color: ${colors.grey0};

  @media (max-width: ${MEDIA_MOBILE_BREAKPOINT}rem) {
    flex: 0 0 auto;
  }
`;

const UploadSections = styled(Container)`
  background-color: ${colors.grey0};
  gap: 1rem;
  @media (max-width: ${MEDIA_MOBILE_BREAKPOINT}rem) {
    box-shadow: unset;
    padding: 0rem;
  }
`;
